// Added by Tim Andersson
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Matches every character that can't be put on a sign. Keep in sync with the `pattern`
 * attribute in blocks/custom-sign-text.liquid.
 */
const DISALLOWED_CHARACTERS = /[^A-Z0-9ÅÄÖÉ &,.@\/-]/g;

/**
 * Like DISALLOWED_CHARACTERS, for products with the `supports_extended_character_set`
 * metafield. Keep in sync with `extended_allowed_pattern` in blocks/custom-sign-text.liquid.
 */
const DISALLOWED_EXTENDED_CHARACTERS = /[^A-Z0-9ÅÄÖÉÜÆØ ,.!?;:@&\/()%=+#−€$£<>*"'-]/g;

/**
 * @param {string} value
 * @param {RegExp} disallowed - The characters to remove.
 * @returns {string} The value uppercased and in composed form (NFC), with every disallowed
 * character removed. Composing first means a decomposed É (E followed by a combining acute
 * accent) is kept as a single É instead of losing its accent.
 */
function sanitize(value, disallowed) {
  return value.normalize('NFC').toUpperCase().replace(disallowed, '');
}

/**
 * Re-renders the custom sign text fields when the shopper selects a different variant, and
 * makes sure the submitted text is uppercased and only contains characters we can make signs with.
 *
 * We do the uppercasing here as well since the CSS `text-transform: uppercase` rule only affects
 * the visual appearance, not the actual value that's visible to the customer in their cart.
 * @extends {Component<{}>}
 */
class CustomSignText extends Component {
  /**
   * @type {Element | null}
   */
  #listenTarget = null;

  connectedCallback() {
    super.connectedCallback();
    this.#listenTarget = this.closest('.shopify-section, dialog');
    this.#listenTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#listenTarget = null;
  }

  /**
   * @param {Event} event - The input event.
   */
  handleInput(event) {
    const { target } = event;

    if (target instanceof HTMLInputElement && target.dataset.line) this.#sanitize(target);
  }

  /**
   * Rewrites an input's value in uppercase with disallowed characters removed, keeping the
   * caret where the shopper left it.
   * @param {HTMLInputElement} input
   */
  #sanitize(input) {
    const disallowed = this.hasAttribute('data-extended-character-set')
      ? DISALLOWED_EXTENDED_CHARACTERS
      : DISALLOWED_CHARACTERS;
    const current = input.value;
    const sanitized = sanitize(current, disallowed);

    if (sanitized === current) return;

    const { selectionStart, selectionEnd } = input;

    input.value = input.maxLength > 0 ? sanitized.slice(0, input.maxLength) : sanitized;

    // Whatever was removed before the caret shifts it left by the same amount.
    if (selectionStart !== null && selectionEnd !== null) {
      input.setSelectionRange(
        Math.min(sanitize(current.slice(0, selectionStart), disallowed).length, input.value.length),
        Math.min(sanitize(current.slice(0, selectionEnd), disallowed).length, input.value.length)
      );
    }
  }

  /**
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const next = detail.html.querySelector('custom-sign-text');
        if (!next) return;

        // Carry over the customer's entered text when switching between variants.
        const typed = this.#readLines();
        morph(this, next, { childrenOnly: true });
        this.#restoreLines(typed);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[custom-sign-text] Event promise rejected:', error);
      });
  };

  /**
   * Reads the non-empty line inputs, keyed by their line number.
   * @returns {Map<string, string>}
   */
  #readLines() {
    const lines = new Map();

    for (const input of this.#lineInputs()) {
      if (input.value && input.dataset.line) lines.set(input.dataset.line, input.value);
    }

    return lines;
  }

  /**
   * Writes previously typed lines back into the matching inputs. Lines the new variant
   * doesn't have are dropped.
   * @param {Map<string, string>} lines
   */
  #restoreLines(lines) {
    for (const input of this.#lineInputs()) {
      const value = input.dataset.line ? lines.get(input.dataset.line) : undefined;
      if (value == null) continue;
      input.value = input.maxLength > 0 ? value.slice(0, input.maxLength) : value;
      this.#sanitize(input);
    }
  }

  /** @returns {NodeListOf<HTMLInputElement>} */
  #lineInputs() {
    return this.querySelectorAll('input[data-line]');
  }
}

if (!customElements.get('custom-sign-text')) {
  customElements.define('custom-sign-text', CustomSignText);
}
